import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuthMethods } from "./session";
import { CircleNotch } from "@phosphor-icons/react";
import { Button } from "../design-system";
import { logger } from "../services/telemetry";
import { beginAuthAttempt, failAuthAttempt } from "./authAttempt";
import {
  identifyUser,
  trackButtonPressed,
  trackSignupFailed,
  trackSignupSucceeded,
  type AuthMethod,
} from "../analytics/tracking";
import { redirectIfEmailNotAllowed, signupErrorMessage } from "./authErrors";
import { useEmailPasswordAuth } from "../services/flags";
import { CAPTCHA_CONTAINER_ID, initSignupCaptcha } from "./firebase";
import { LOGIN_PATH } from "./paths";
import { markPendingInvitation } from "./postAuthIntent";
import {
  AuthCard,
  AuthCta,
  AuthField,
  AuthHeading,
  AuthFrame,
  EMAIL_RE,
  GoogleColorIcon,
  PasswordField,
  TermsNotice,
} from "./AuthShared";

/* Centered single-column sign-up, the companion to LoginScreen. Collects email
 * + password only (no name/terms/confirm — the displayName derives from the
 * email and the org/user are provisioned by the identity service). A new
 * account is unverified, so a successful sign-up flips the auth status to
 * "unverified" and SignupRoute forwards to /check-email.
 *
 * Invitation deeplinks: invite emails land on /auth?invitation_id=… and the
 * sign-up link carries the query here, so we read invitation_id and thread it
 * into signupWithEmail → /sign-in, joining the invited org directly instead of
 * creating a throwaway one. Social sign-up is the same full-page redirect as
 * login. */

/* Move an invite deeplink's invitation_id out of the URL and into storage.
 *
 * The id is a capability token — on its own it accepts the org invitation — and
 * UserSignUpSucceeded fires (as a GTM marketing conversion, OPSS-249) while the
 * browser is still on /signup?invitation_id=…, where a tag reading the built-in
 * Page URL variable would hand the token to a third party. Stash it, then
 * replaceState it away; nextAfterAuth falls back to storage, so the /invite/:id
 * hop still works on both the email and social paths.
 *
 * replaceState deliberately, not a router navigation: this must not remount the
 * screen mid-sign-up, and React Router never observes it. The already-captured
 * `invitationId` (and the storage entry) are what the flow actually reads, so
 * nothing depends on the router's now-stale search string. */
function useStripInvitationFromUrl(invitationId: string | undefined): void {
  useEffect(() => {
    if (!invitationId) return;

    // Keep the URL param when storage is unavailable — it's then the only copy,
    // and nextAfterAuth still reads it from the (unmodified) router location.
    if (!markPendingInvitation(invitationId)) return;

    const url = new URL(window.location.href);
    if (!url.searchParams.has("invitation_id")) return;

    url.searchParams.delete("invitation_id");
    window.history.replaceState(window.history.state, "", url.toString());
  }, [invitationId]);
}

export function SignupScreen() {
  const authMethods = useAuthMethods();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get("invitation_id") ?? undefined;
  useStripInvitationFromUrl(invitationId);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [passwordErr, setPasswordErr] = useState("");
  // Which auth method is in flight (null when idle). Tracks the *specific* CTA
  // clicked so only that button shows a spinner; `busy` still disables the
  // whole form to block a concurrent second attempt.
  const [pending, setPending] = useState<AuthMethod | null>(null);
  const busy = pending !== null;
  const [formErr, setFormErr] = useState<string | null>(null);
  // Email/password is flag-gated (igepa — the debugging path); Google is the
  // product default. Held until `ready` so the form doesn't flash in and out.
  const emailAuth = useEmailPasswordAuth();
  const showEmailForm = emailAuth.ready && emailAuth.enabled;

  // Pre-arm the invisible reCAPTCHA so the challenge is ready before submit
  // (no-op unless captcha is enabled). Mirrors studio's sign-up gate. Only
  // needed for the flag-gated email/password path.
  useEffect(() => {
    if (showEmailForm) initSignupCaptcha();
  }, [showEmailForm]);

  // On success the status flips to "unverified" and SignupRoute forwards to
  // /check-email, so this screen unmounts — we only clear `pending` on failure.
  // Fires AuthStarted on the attempt and UserSignUpFailed on a rejected one.
  const run = async (authMethod: AuthMethod, fn: () => Promise<unknown>) => {
    setFormErr(null);
    trackButtonPressed(
      authMethod === "GOOGLE" ? "Continue with Google" : "Create account",
    );
    beginAuthAttempt(authMethod, "SIGN_UP");
    setPending(authMethod);
    try {
      await fn();
    } catch (err) {
      /* Terminate the attempt FIRST: the allowlist branch leaves via
       * window.location, so nothing after it runs — which is why allowlist
       * rejections never appeared in the funnel at all. */
      failAuthAttempt(err);

      // Allowlist rejection is terminal — redirect to coming-soon, skip the inline error.
      if (redirectIfEmailNotAllowed(err)) return;

      logger.error(
        err instanceof Error ? err : new Error(String(err)),
        `${authMethod} sign-up failed`,
      );
      trackSignupFailed(authMethod, err);
      setFormErr(signupErrorMessage(err));
      setPending(null);
    }
  };

  const handleSocial = () => run("GOOGLE", () => authMethods.socialLogin());

  const validate = () => {
    const trimmed = email.trim();
    const e = !trimmed
      ? "Enter your email."
      : !EMAIL_RE.test(trimmed)
        ? "Enter a valid email address."
        : "";
    const p = !password
      ? "Create a password."
      : password.length < 8
        ? "Use at least 8 characters."
        : "";
    setEmailErr(e);
    setPasswordErr(p);
    return !e && !p;
  };

  const handleEmail = (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    void run("EMAIL_PASSWORD", async () => {
      const user = await authMethods.signupWithEmail(
        email.trim(),
        password,
        invitationId,
      );
      // Identify before the success event so it carries user_id: the new
      // account is unverified, so AuthTelemetryBridge (which identifies on the
      // authenticated status) hasn't run yet.
      identifyUser(user.userId, user.organizationId);
      trackSignupSucceeded("EMAIL_PASSWORD");
    });
  };

  return (
    <AuthFrame>
      <div className="onboarding onboarding-card flex flex-col gap-5">
        <AuthCard>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1.5">
              <AuthHeading>Create your account</AuthHeading>
              <p className="t-body-sm" style={{ color: "var(--fg-3)" }}>
                {showEmailForm
                  ? "Continue with Google, or use your work email and a password."
                  : "Continue with your Google account."}
              </p>
            </div>

            {formErr && (
              <div
                role="alert"
                className="flex animate-ai21-rise items-start gap-2 rounded-md border border-error-border bg-error-bg px-3 py-2.5 [&_svg]:size-4 [&_svg]:shrink-0"
              >
                <span className="text-body-sm text-error-fg">{formErr}</span>
              </div>
            )}

            {/* Social provider (full-page redirect) — offered first, above the
                email/password form. */}
            <Button
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={handleSocial}
              disabled={busy}
              iconLeft={
                pending === "GOOGLE" ? (
                  <CircleNotch className="animate-spin" />
                ) : (
                  <GoogleColorIcon />
                )
              }
            >
              Continue with Google
            </Button>

            {showEmailForm && (
              <>
                <div className="flex items-center gap-3">
                  <div
                    className="h-px flex-1"
                    style={{ background: "var(--onb-track)" }}
                  />
                  <span className="text-body-sm text-fg-3">or</span>
                  <div
                    className="h-px flex-1"
                    style={{ background: "var(--onb-track)" }}
                  />
                </div>

                <form
                  onSubmit={handleEmail}
                  className="flex flex-col gap-4"
                  noValidate
                >
                  <AuthField
                    id="email"
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(v) => {
                      setEmail(v);
                      if (emailErr) setEmailErr("");
                    }}
                    error={emailErr}
                    placeholder="you@company.com"
                    autoComplete="email"
                    disabled={busy}
                    autoFocus
                  />
                  <PasswordField
                    id="password"
                    label="Password"
                    value={password}
                    onChange={(v) => {
                      setPassword(v);
                      if (passwordErr) setPasswordErr("");
                    }}
                    error={passwordErr}
                    autoComplete="new-password"
                    disabled={busy}
                  />
                  <AuthCta
                    type="submit"
                    className="w-full"
                    disabled={busy}
                    iconLeft={
                      pending === "EMAIL_PASSWORD" ? (
                        <CircleNotch className="animate-spin" />
                      ) : undefined
                    }
                  >
                    Create account
                  </AuthCta>
                </form>
              </>
            )}

            <TermsNotice />

            {/* Invisible reCAPTCHA mount (renders a corner badge only when
                enabled; the challenge runs on sign-up submit). */}
            <div id={CAPTCHA_CONTAINER_ID} />
          </div>
        </AuthCard>

        {/* Google-only mode has no separate sign-in: "Continue with Google"
            signs an existing user in by itself, so the cross-link is noise. */}
        {showEmailForm && (
          <p
            className="text-center text-[12px]"
            style={{ color: "var(--fg-3)" }}
          >
            Already have an account?{" "}
            <Link
              to={`${LOGIN_PATH}${search}`}
              className="font-medium no-underline hover:underline"
              style={{ color: "var(--fg-1)" }}
              onClick={() => trackButtonPressed("Sign in")}
            >
              Sign in
            </Link>
          </p>
        )}
      </div>
    </AuthFrame>
  );
}
